import React, { useEffect, useState } from "react";
import axios from "src/config/axios";
import { Badge, Button, Spinner, Table } from "react-bootstrap";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  showSuccessToast,
  showValidationErrorToast,
} from "src/components/Toasts";
import { statusVariants } from "./helpers";

const MonthlyReportPage = () => {
  const { t } = useTranslation("dashboard");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runningAction, setRunningAction] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const previousMonth = moment().subtract(1, "month");

  useEffect(() => {
    loadReports();
  }, []);

  function loadReports() {
    setLoading(true);
    setErrorMessage(null);

    axios
      .get("/api/v1/local_tax_monthly_reports.json")
      .then(({ data }) => {
        setReports(data.entries || []);
      })
      .catch((error) => {
        console.error(error);
        showValidationErrorToast(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function prepareMonth(report) {
    const actionKey = actionKeyFor(report, "prepare");
    setRunningAction(actionKey);
    setErrorMessage(null);

    axios
      .post("/api/v1/local_tax_monthly_reports/prepare_month.json", {
        month: report.month,
        year: report.year,
      })
      .then(({ data }) => {
        replaceReport(data);
        showSuccessToast(t("local_tax_monthly_reports.messages.prepared"));
      })
      .catch((error) => {
        handleMonthlyError(
          error,
          t("local_tax_monthly_reports.messages.prepare_failed")
        );
      })
      .finally(() => {
        setRunningAction(null);
      });
  }

  function closeMonth(report) {
    const actionKey = actionKeyFor(report, "close");
    setRunningAction(actionKey);
    setErrorMessage(null);

    axios
      .post("/api/v1/local_tax_monthly_reports/close_month.json", {
        month: report.month,
        year: report.year,
      })
      .then(({ data }) => {
        replaceReport(data);
        showSuccessToast(t("local_tax_monthly_reports.messages.closed"));
      })
      .catch((error) => {
        handleMonthlyError(
          error,
          t("local_tax_monthly_reports.messages.close_failed")
        );
      })
      .finally(() => {
        setRunningAction(null);
      });
  }

  function handleMonthlyError(error, fallbackMessage) {
    console.error(error);

    if (error.response?.data) {
      replaceReport(error.response.data);
      setErrorMessage(
        error.response.data.error_message ||
          error.response.data.daily_reports_blocking_reason ||
          error.response.data.errors?.join(", ") ||
          fallbackMessage
      );
    } else {
      setErrorMessage(fallbackMessage);
    }

    showValidationErrorToast(error);
  }

  function replaceReport(updatedReport) {
    if (!updatedReport?.year || !updatedReport?.month) return;

    setReports((currentReports) =>
      currentReports.map((report) =>
        report.year === updatedReport.year &&
        report.month === updatedReport.month
          ? { ...report, ...updatedReport, exists: true }
          : report
      )
    );
  }

  function actionKeyFor(report, action) {
    return `${report.year}-${report.month}-${action}`;
  }

  function statusLabel(status) {
    return t("local_tax_daily_reports.statuses." + status, {
      defaultValue: status || "-",
    });
  }

  function statusVariant(status) {
    return statusVariants[status] || "secondary";
  }

  function tableValue(value) {
    return value === null || value === undefined ? "-" : value;
  }

  function monthLabel(report) {
    return moment({
      year: report.year,
      month: report.month - 1,
      date: 1,
    }).format("MMMM YYYY");
  }

  function dailyReportsLabel(report) {
    return report.daily_reports_completed
      ? t("local_tax_monthly_reports.daily_reports.completed")
      : t("local_tax_monthly_reports.daily_reports.incomplete");
  }

  function isPreviousMonth(report) {
    return (
      report.year === previousMonth.year() &&
      report.month === previousMonth.month() + 1
    );
  }

  function renderAction(report) {
    const prepareKey = actionKeyFor(report, "prepare");
    const closeKey = actionKeyFor(report, "close");

    if (report.status === "closed") {
      return (
        <span className="text-muted">
          {t("local_tax_monthly_reports.actions.closed")}
        </span>
      );
    }

    if (report.token) {
      return (
        <Button
          size="sm"
          variant="success"
          onClick={() => closeMonth(report)}
          disabled={Boolean(runningAction)}
        >
          {runningAction === closeKey && (
            <Spinner animation="border" className="mr-2" size="sm" />
          )}
          {t("local_tax_monthly_reports.actions.close_month")}
        </Button>
      );
    }

    return (
      <Button
        size="sm"
        variant={isPreviousMonth(report) ? "primary" : "secondary"}
        onClick={() => prepareMonth(report)}
        disabled={Boolean(runningAction) || !report.daily_reports_completed}
        title={
          report.daily_reports_completed
            ? undefined
            : report.daily_reports_blocking_reason
        }
      >
        {runningAction === prepareKey && (
          <Spinner animation="border" className="mr-2" size="sm" />
        )}
        {t("local_tax_monthly_reports.actions.prepare_month")}
      </Button>
    );
  }

  return (
    <div className="pt-3">
      {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

      {loading ? (
        <div className="text-center p-4">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <div className="ui-table-container mb-3">
          <Table responsive>
            <thead>
              <tr>
                <th>{t("local_tax_monthly_reports.table.month")}</th>
                <th>{t("local_tax_monthly_reports.table.status")}</th>
                <th>{t("local_tax_monthly_reports.table.daily_reports")}</th>
                <th>{t("local_tax_monthly_reports.table.total_tax_adults")}</th>
                <th>{t("local_tax_monthly_reports.table.total_tax_childs")}</th>
                <th>
                  {t("local_tax_monthly_reports.table.total_tax_exempted")}
                </th>
                <th>{t("local_tax_monthly_reports.table.payment_pdf")}</th>
                <th>{t("local_tax_monthly_reports.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={`${report.year}-${report.month}`}>
                  <td>{monthLabel(report)}</td>
                  <td>
                    <Badge
                      pill
                      variant={statusVariant(report.status)}
                      text="light"
                    >
                      {statusLabel(report.status)}
                    </Badge>
                  </td>
                  <td>
                    <Badge
                      pill
                      variant={
                        report.daily_reports_completed ? "success" : "warning"
                      }
                      text="light"
                    >
                      {dailyReportsLabel(report)}
                    </Badge>
                  </td>
                  <td>{tableValue(report.total_tax_adults)}</td>
                  <td>{tableValue(report.total_tax_childs)}</td>
                  <td>{tableValue(report.total_tax_exempted)}</td>
                  <td>
                    {report.payment_pdf_url ? (
                      <a
                        href={report.payment_pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t(
                          "local_tax_monthly_reports.actions.open_payment_pdf"
                        )}
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="text-right text-nowrap">
                    {renderAction(report)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default MonthlyReportPage;
